import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { recruitmentAdminApi } from '../../../services/api'
import Button from '../../../components/ui/Button'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Card from '../../../components/ui/Card'
import AdminFormField, { adminInputClass } from '../../../components/admin/AdminFormField'

const EMPTY = { key: '', name_en: '', name_ar: '', color: '#64748b', sort_order: 0, is_default: false, is_final: false }

export default function AdminJobStatuses() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    recruitmentAdminApi.statuses
      .getAll()
      .then(({ data }) => setItems(data.data || data || []))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (form.id) await recruitmentAdminApi.statuses.update(form.id, form)
      else await recruitmentAdminApi.statuses.create(form)
      setForm(null)
      load()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={() => setForm(EMPTY)} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.recruitment.addStatus')}
        </Button>
      </div>

      {form && (
        <Card className="mb-6">
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            <AdminFormField label={t('admin.recruitment.fields.key')} htmlFor="status-key">
              <input
                id="status-key"
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                className={adminInputClass}
                required
              />
            </AdminFormField>
            <AdminFormField label={t('admin.recruitment.fields.nameEn')} htmlFor="status-name-en">
              <input
                id="status-name-en"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className={adminInputClass}
                required
              />
            </AdminFormField>
            <AdminFormField label={t('admin.recruitment.fields.nameAr')} htmlFor="status-name-ar">
              <input
                id="status-name-ar"
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                dir="rtl"
                lang="ar"
                className={`${adminInputClass} font-ar`}
                required
              />
            </AdminFormField>
            <AdminFormField label={t('admin.recruitment.fields.color')} htmlFor="status-color">
              <input
                id="status-color"
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-10 w-full rounded-xl border border-border"
              />
            </AdminFormField>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
              {t('admin.recruitment.fields.isDefault')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_final} onChange={(e) => setForm({ ...form, is_final: e.target.checked })} />
              {t('admin.recruitment.fields.isFinal')}
            </label>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>{t('buttons.save')}</Button>
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>{t('buttons.cancel')}</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full" style={{ backgroundColor: item.color }} />
                <div>
                  <div className="font-semibold text-primary">{item.name_en}</div>
                  <div className="text-sm text-text/60">{item.key}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => setForm(item)} className="p-2 text-primary"><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={async () => { await recruitmentAdminApi.statuses.delete(item.id); load() }} className="p-2 text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
